// Archivo: src/components/client/ServerControls.tsx

import React, { useState } from 'react';
import { Server as ServerType } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Play, Square, RotateCcw, CalendarPlus, X, Loader2, BarChart2, Shield, Settings } from 'lucide-react'; 
import { ServerTrafficModal } from '../admin/ServerTrafficModal';
import { ServerBandwidthModal } from '../admin/ServerBandwidthModal'; 
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/dateUtils';

// --- COMPONENTE: MODAL DE MOTIVO ---
const ReasonModal: React.FC<{
    action: 'start' | 'stop' | 'restart';
    serverName: string;
    onConfirm: (reason: string) => void;
    onCancel: () => void;
    isSubmitting: boolean;
}> = ({ action, serverName, onConfirm, onCancel, isSubmitting }) => {
    const [reason, setReason] = useState('');
    const actionText = { start: 'Iniciar', stop: 'Detener', restart: 'Reiniciar' };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
            <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Confirmar Acción</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-gray-600 mb-4 text-sm">
                    Vas a <span className="font-bold text-gray-900">{actionText[action]}</span> el servidor <span className="font-bold text-gray-900">{serverName}</span>.
                </p>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Motivo (opcional)..."
                    className="w-full p-3 border border-gray-200 rounded-xl h-28 outline-none text-sm"
                />
                <div className="flex justify-end space-x-3 mt-6">
                    <button onClick={onCancel} className="bg-gray-100 text-gray-600 px-5 py-2 rounded-xl hover:bg-gray-200 transition-colors">Cancelar</button>
                    <button onClick={() => onConfirm(reason)} disabled={isSubmitting} className="bg-blue-600 text-white px-5 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center transition-all">
                        {isSubmitting && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
                        Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- CONFIGURACIÓN DE SISTEMAS OPERATIVOS (ICONOS REPARADOS) ---
const OS_CONFIG: Record<string, { name: string; versions: string[]; icon: string }> = {
  ubuntu: { 
    name: 'Ubuntu', 
    versions: ['24.04', '22.04', '20.04', '18.04'], 
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ubuntu/ubuntu-original.svg' 
  },
  alma: { 
    name: 'AlmaLinux', 
    versions: ['10.0 x64', '9.x', '8.x'], 
    icon: 'https://raw.githubusercontent.com/AlmaLinux/almalinux-artwork/master/Logos/AlmaLinux-Logo-Symbol-Color.svg' 
  },
  debian: { 
    name: 'Debian', 
    versions: ['13 Trixie', '12', '11'], 
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/debian/debian-original.svg' 
  },
  fedora: { 
    name: 'Fedora', 
    versions: ['38 x64', '39', '40'], 
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/fedora/fedora-original.svg' 
  },
  rocky: { 
    name: 'Rocky Linux', 
    versions: ['10.0 x64', '9.x', '8.x'], 
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rockylinux/rockylinux-original.svg' 
  },
  windows: { 
    name: 'Windows', 
    versions: ['Server 2025', '2022', '2019'], 
    icon: 'https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/windows.svg' 
  },
  freebsd: { 
    name: 'FreeBSD', 
    versions: ['14.3 x64', '13.x'], 
    icon: 'https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/freebsd.svg' 
  },
  centos: { 
    name: 'CentOS', 
    versions: ['Stream 10.0', 'Stream 9'], 
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/centos/centos-original.svg' 
  },
};

export const ServerControls: React.FC<{ servers: ServerType[], onActionCompleted: () => void }> = ({ servers, onActionCompleted }) => {
    const { user } = useAuth();
    
    // Estados de Modales
    const [modalInfo, setModalInfo] = useState<{ server: ServerType; action: 'start' | 'stop' | 'restart' } | null>(null);
    const [showDDoSModal, setShowDDoSModal] = useState<ServerType | null>(null);
    const [showBandwidthModal, setShowBandwidthModal] = useState<ServerType | null>(null);
    const [showReinstallModal, setShowReinstallModal] = useState<ServerType | null>(null);

    // Estados de Carga
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRenewing, setIsRenewing] = useState<string | null>(null);
    // 👇 ESTADO UNIFICADO: reinstallingServerId
    const [reinstallingServerId, setReinstallingServerId] = useState<string | null>(null);

    // Estados de Reinstalación
    const [selectedOS, setSelectedOS] = useState('ubuntu');
    const [selectedVersion, setSelectedVersion] = useState('24.04');
    const [reinstallPassword, setReinstallPassword] = useState('');

    const handleActionConfirm = async (reason: string) => {
        if (!modalInfo || !user) return;
        setIsSubmitting(true);
        const { server, action } = modalInfo;
        const toastId = toast.loading(`Enviando comando ${action}...`);
        try {
            await api.executeServerAction(server.id, action, {
                reason: reason || 'No se especificó un motivo.',
                clientId: user.id,
                clientName: user.username,
            });
            toast.success(`Acción '${action}' enviada.`, { id: toastId });
            setModalInfo(null);
            setTimeout(() => onActionCompleted(), 2000);
        } catch (error: any) {
            toast.error(`Error: ${error.message}`, { id: toastId });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleExecuteReinstall = async (royaleId: string) => {
        const serverId = showReinstallModal?.id;
        if (!serverId) return;

        if (reinstallPassword.length < 8) {
            toast.error("La contraseña debe tener al menos 8 caracteres.");
            return;
        }
        if (!confirm(`¿Estás SEGURO de borrar todo e instalar ${selectedOS} ${selectedVersion}?`)) return;

        // 👇 ACTIVAMOS LA CARGA CON EL NOMBRE CORRECTO
        setReinstallingServerId(serverId);
        setShowReinstallModal(null);
        
        const toastId = toast.loading("Iniciando reinstalación segura...");
        try {
            const response = await fetch('/api/servers/reinstall', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    royaleServiceId: royaleId, 
                    os: selectedOS, 
                    os_version: selectedVersion, 
                    password: reinstallPassword 
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success("🚀 Reinstalación en curso. El servidor se reiniciará pronto.", { id: toastId });
                setReinstallPassword('');
            } else {
                toast.error(data.message || "Error en el proceso.", { id: toastId });
                setReinstallingServerId(null);
            }
        } catch (error) {
            toast.error("Error de comunicación con el servidor.", { id: toastId });
            setReinstallingServerId(null);
        }
        
        // Mantenemos el spinner visual unos segundos
        setTimeout(() => setReinstallingServerId(null), 10000);
    };

    const handleRenew = async (server: ServerType) => {
        if (!user || !server.stripePriceId) return;
        setIsRenewing(server.id);
        const toastId = toast.loading('Preparando pago...');
        try {
            const session = await api.createCheckoutSession({
                serverId: server.id,
                clientId: user.id,
                priceId: server.stripePriceId,
            });
            if (session?.url) window.location.href = session.url;
        } catch (error: any) {
            toast.error(`Error: ${error.message}`, { id: toastId });
        } finally {
            setIsRenewing(null);
        }
    };

    const isReinstalling = !!reinstallingServerId;

    return (
        <>
            {/* MODALES DE ACCIÓN */}
            {modalInfo && (
                <ReasonModal
                    action={modalInfo.action}
                    serverName={modalInfo.server.name}
                    onConfirm={handleActionConfirm}
                    onCancel={() => setModalInfo(null)}
                    isSubmitting={isSubmitting}
                />
            )}

            {showDDoSModal && (
               <ServerTrafficModal 
                 server={showDDoSModal} 
                 onClose={() => setShowDDoSModal(null)} 
                 isClientPanel={true}
                 clientId={user?.id}
               />
            )}
            
            {showBandwidthModal && (
              <ServerBandwidthModal 
                server={showBandwidthModal} 
                onClose={() => setShowBandwidthModal(null)} 
                clientId={user?.id} 
              />
            )}

            {/* MODAL DE REINSTALACIÓN */}
            {showReinstallModal && (
                <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[70] p-4 backdrop-blur-md overflow-y-auto">
                    <div className="bg-[#0f172a] text-white p-8 rounded-3xl max-w-5xl w-full border border-gray-800 shadow-2xl my-auto">
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h2 className="text-2xl font-bold">Reinstalar el servidor</h2>
                                <p className="text-gray-400 text-sm">Formatea tu disco con una distribución limpia</p>
                            </div>
                            <button onClick={() => !isReinstalling && setShowReinstallModal(null)} className="p-2 hover:bg-gray-800 rounded-full transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            <div className="lg:col-span-2">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    {Object.entries(OS_CONFIG).map(([key, info]) => (
                                        <div 
                                            key={key}
                                            onClick={() => {
                                                if (isReinstalling) return;
                                                setSelectedOS(key);
                                                if (selectedOS !== key) setSelectedVersion(info.versions[0]);
                                            }}
                                            className={`p-4 rounded-2xl border-2 transition-all cursor-pointer text-center flex flex-col items-center gap-3 ${selectedOS === key ? 'border-blue-500 bg-blue-500/10 shadow-[0_0_20px_rgba(59,130,246,0.2)]' : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'}`}
                                        >
                                            <div className="h-10 w-10 flex items-center justify-center">
                                                <img src={info.icon} className="max-h-full max-w-full object-contain" alt={info.name} />
                                            </div>
                                            <div className="font-bold text-xs truncate w-full">{info.name}</div>
                                            
                                            <select 
                                                disabled={isReinstalling}
                                                className="w-full bg-black text-[10px] p-2 rounded-lg border border-gray-700 outline-none focus:border-blue-500 cursor-pointer"
                                                value={selectedOS === key ? selectedVersion : info.versions[0]}
                                                onClick={(e) => e.stopPropagation()} 
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedOS(key);
                                                    setSelectedVersion(e.target.value);
                                                }}
                                            >
                                                {info.versions.map(v => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col justify-between bg-gray-900/50 p-6 rounded-2xl border border-gray-800">
                                <div className="space-y-4">
                                    <label className="block text-sm font-semibold text-gray-300">Nueva Contraseña Raíz (root) *</label>
                                    <input 
                                        type="password" 
                                        disabled={isReinstalling}
                                        className="w-full bg-black border border-gray-700 rounded-xl p-3 focus:border-blue-500 outline-none transition-all font-mono"
                                        placeholder="••••••••••••"
                                        value={reinstallPassword}
                                        onChange={(e) => setReinstallPassword(e.target.value)}
                                    />
                                    <div className="flex gap-1 h-1">
                                        <div className={`flex-1 rounded-full ${reinstallPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-800'}`}></div>
                                        <div className={`flex-1 rounded-full ${/[A-Z]/.test(reinstallPassword) ? 'bg-green-500' : 'bg-gray-800'}`}></div>
                                        <div className={`flex-1 rounded-full ${/[0-9]/.test(reinstallPassword) ? 'bg-green-500' : 'bg-gray-800'}`}></div>
                                    </div>
                                    <p className="text-[10px] text-gray-500 italic">Mínimo 8 caracteres, números y una mayúscula.</p>
                                </div>
                                
                                <button 
                                    disabled={isReinstalling || !reinstallPassword}
                                    onClick={() => handleExecuteReinstall(showReinstallModal.royaleServiceId || '')}
                                    className={`w-full mt-8 py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${isReinstalling ? 'bg-gray-800 cursor-wait text-gray-500' : 'bg-red-600 hover:bg-red-500 shadow-xl shadow-red-900/20 active:scale-95'}`}
                                >
                                    {isReinstalling ? (
                                        <><Loader2 className="w-6 h-6 animate-spin" /><span>Procesando...</span></>
                                    ) : (
                                        <span>Reinstalar servidor</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* LISTADO DE TARJETAS DE SERVIDOR */}
            {servers.length === 0 ? (
                <div className="text-center text-gray-500 py-16 bg-white rounded-3xl border-2 border-dashed border-gray-100 italic">No hay servidores asignados a tu cuenta.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {servers.map(server => (
                        <div key={server.id} className="relative bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-xl hover:shadow-gray-200/40 transition-all duration-300 overflow-hidden">
                            
                            {/* CAPA DE CARGA (SPINNER) SOBRE LA TARJETA */}
                            {reinstallingServerId === server.id && (
                                <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] z-20 flex flex-col items-center justify-center text-center p-4">
                                    <Loader2 className="w-10 h-10 animate-spin text-orange-500 mb-2" />
                                    <span className="font-black text-orange-600 text-xs uppercase tracking-widest">Reinstalando SO...</span>
                                </div>
                            )}

                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-black text-gray-900 tracking-tight flex items-center">
                                        {server.name}
                                        {/* Spinner pequeño en el título */}
                                        {reinstallingServerId === server.id && (
                                            <Loader2 className="ml-2 w-5 h-5 animate-spin text-orange-500" />
                                        )}
                                    </h3>
                                    <span className={`px-3 py-1 text-[10px] uppercase font-black rounded-full ${server.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {server.status === 'online' ? '● En línea' : '○ Offline'}
                                    </span>
                                </div>
                                <div className="space-y-2 text-sm text-gray-500 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                    {/* CORRECCIÓN IP: 'notranslate' evita errores de traducción */}
                                    <p className="flex justify-between border-b border-gray-50 pb-1">
                                        <span className="notranslate">Dirección IP:</span> 
                                        <span className="text-gray-900 font-mono font-bold">{server.ip}</span>
                                    </p>
                                    <p className="flex justify-between">
                                        <span>Vencimiento:</span> 
                                        <span className="text-gray-900 font-bold">{formatDate(server.expirationDate)}</span>
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 space-y-3">
                                <div className="grid grid-cols-3 gap-2">
                                    <button onClick={() => setModalInfo({ server, action: 'start' })} className="bg-green-500 text-white p-2.5 rounded-xl hover:bg-green-600 transition-all flex items-center justify-center"><Play className="w-4 h-4" /></button>
                                    <button onClick={() => setModalInfo({ server, action: 'stop' })} className="bg-red-500 text-white p-2.5 rounded-xl hover:bg-red-600 transition-all flex items-center justify-center"><Square className="w-4 h-4" /></button>
                                    <button onClick={() => setModalInfo({ server, action: 'restart' })} className="bg-blue-500 text-white p-2.5 rounded-xl hover:bg-blue-600 transition-all flex items-center justify-center"><RotateCcw className="w-4 h-4" /></button>
                                </div>

                                <button 
                                    onClick={() => setShowReinstallModal(server)}
                                    className="w-full bg-orange-600 text-white p-3 rounded-xl hover:bg-orange-700 flex items-center justify-center gap-2 font-black text-xs uppercase tracking-wider transition-all shadow-md shadow-orange-100 active:scale-95"
                                >
                                    <Settings className="w-4 h-4" /> REINSTALAR SISTEMA
                                </button>

                                <div className="grid grid-cols-2 gap-2">
                                    <button onClick={() => setShowDDoSModal(server)} className="bg-slate-800 text-white p-2.5 rounded-xl hover:bg-black transition-all text-[10px] font-bold uppercase tracking-tighter flex items-center justify-center gap-1"><Shield className="w-3 h-3" /> DDoS</button>
                                    <button onClick={() => setShowBandwidthModal(server)} className="bg-slate-800 text-white p-2.5 rounded-xl hover:bg-black transition-all text-[10px] font-bold uppercase tracking-tighter flex items-center justify-center gap-1"><BarChart2 className="w-3 h-3" /> TRÁFICO</button>
                                </div>

                                {server.stripePriceId && (
                                    <button 
                                        onClick={() => handleRenew(server)} 
                                        disabled={isRenewing === server.id} 
                                        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3.5 rounded-xl flex items-center justify-center font-black text-xs uppercase tracking-widest shadow-lg shadow-purple-100 disabled:opacity-50 transition-all"
                                    >
                                        {isRenewing === server.id ? <Loader2 className="animate-spin w-5 h-5" /> : 'Renovar Servidor'}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};